import { http } from "@/api/http";
import type { AxiosRequestConfig } from "axios";
import type {
  BrowserFillReport,
  CandidateState,
  DraftPreview,
  HistoryResult,
  MailboxOAuthStatusResult,
  PdfExtractionResult,
  ProfileExtractionResult,
  ProfileResult,
  SessionLogResult,
  SubmissionResult,
} from "@/types/candidatePages";
import type { Application } from "@/types/application";
import type { CandidateProfile } from "@/types/profile";

function signalConfig(signal?: AbortSignal): AxiosRequestConfig {
  const config: AxiosRequestConfig = {};
  if (signal) config.signal = signal;
  return config;
}

export async function getCandidateState(signal?: AbortSignal): Promise<CandidateState> {
  const response = signal
    ? await http.get<CandidateState>("/state", { signal })
    : await http.get<CandidateState>("/state");
  return response.data;
}

export async function saveCandidateProfile(
  profile: CandidateProfile,
  signal?: AbortSignal,
): Promise<ProfileResult> {
  const response = await http.post<ProfileResult>("/profile", profile, signalConfig(signal));
  return response.data;
}

export async function extractCandidateProfile(
  filename: string,
  cvText: string,
  signal?: AbortSignal,
): Promise<ProfileExtractionResult> {
  const response = await http.post<ProfileExtractionResult>("/profile/extract", {
    cv_text: cvText,
    filename,
  }, signalConfig(signal));
  return response.data;
}

export async function extractPdfText(
  data: string,
  signal?: AbortSignal,
): Promise<PdfExtractionResult> {
  const response = await http.post<PdfExtractionResult>(
    "/files/extract-pdf",
    { data },
    signalConfig(signal),
  );
  return response.data;
}

export async function getMailboxOAuthStatus(
  signal?: AbortSignal,
): Promise<MailboxOAuthStatusResult> {
  const response = signal
    ? await http.get<MailboxOAuthStatusResult>("/inbox/oauth-status", { signal })
    : await http.get<MailboxOAuthStatusResult>("/inbox/oauth-status");
  return response.data;
}

export async function getApplicationHistory(signal?: AbortSignal): Promise<HistoryResult> {
  const response = signal
    ? await http.get<HistoryResult>("/history", { signal })
    : await http.get<HistoryResult>("/history");
  return response.data;
}

export async function restoreArchivedApplication(
  historyId: string,
  signal?: AbortSignal,
): Promise<Application> {
  const response = await http.post<{ application: Application }>(
    `/tracking/${encodeURIComponent(historyId)}/restore`,
    {},
    signalConfig(signal),
  );
  return response.data.application;
}

export async function previewDraft(draftId: string, signal?: AbortSignal): Promise<DraftPreview> {
  const response = await http.post<DraftPreview>(
    "/draft/preview",
    { draft_id: draftId },
    signalConfig(signal),
  );
  return response.data;
}

export async function launchDraftBrowser(
  draftId: string,
  signal?: AbortSignal,
): Promise<BrowserFillReport> {
  const response = await http.post<BrowserFillReport>("/draft/launch-browser-fill", {
    draft_id: draftId,
  }, signalConfig(signal));
  return response.data;
}

export async function runDraftAutopilot(
  draftId: string,
  sessionId: string,
  signal?: AbortSignal,
): Promise<BrowserFillReport> {
  const response = await http.post<BrowserFillReport>("/draft/autopilot", {
    draft_id: draftId,
    session_id: sessionId,
  }, signalConfig(signal));
  return response.data;
}

export async function getDraftSessionLog(
  sessionId: string,
  signal?: AbortSignal,
): Promise<SessionLogResult> {
  const config: AxiosRequestConfig = {
    params: { session_id: sessionId },
  };
  if (signal) config.signal = signal;
  const response = await http.get<SessionLogResult>("/draft/session-log", config);
  return response.data;
}

export async function approveDraft(
  draftId: string,
  signal?: AbortSignal,
): Promise<SubmissionResult> {
  const response = await http.post<SubmissionResult>(
    "/submit",
    { approved: true, draft_id: draftId },
    signalConfig(signal),
  );
  return response.data;
}
